import { Component, ChangeDetectionStrategy, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { AuthService, User } from '@auth0/auth0-angular';
import {
  AdminAuthService,
  AnalyticsService,
  BrandingService,
  UserTypeEnum,
  RouterUrlsEnum,
  AdminShellComponent,
  LoadingSpinnerComponent,
  ToastContainerComponent,
} from '@bodyanalytics/ui-core';
import { Subscription, filter } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-root',
  templateUrl: './app.component.html',
  imports: [AsyncPipe, RouterOutlet, AdminShellComponent, LoadingSpinnerComponent, ToastContainerComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  protected auth = inject(AuthService);
  private analyticsService = inject(AnalyticsService);
  private router = inject(Router);
  private adminAuthService = inject(AdminAuthService);
  private branding = inject(BrandingService);
  /** Tenant whose branding is currently applied — avoids a refetch per navigation. */
  private brandedTenantId: string | null = null;
  /** Tenant whose branding fetch is in flight — dedupes back-to-back navigations. */
  private pendingTenantId: string | null = null;
  private authSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;

  /**
   * #1553 Scope A: true for authenticated `/auth/...` portal pages, which
   * render the full admin shell (top-nav + sidebar). Public /
   * pre-auth pages (`login` (plus the legacy `home` redirect), `error`, and — the actual bug — a
   * non-admin's `/access-denied` splash) must NOT get that chrome: an
   * SSO-authenticated non-admin has `auth.isAuthenticated$ === true`, so the
   * old unconditional `<bodyanalytics-admin-shell />` rendered the full
   * sidebar/topbar chrome around what should have been a standalone
   * dead-end screen with no way out. (The shell's mobile tab bar was removed
   * 2026-09-08 — its destinations are reachable from the Manage grid now.)
   * Mirrors the customer app's
   * `isPortalRoute()` gate (`apps/front/customer/src/app/app.component.ts`).
   */
  protected readonly isPortalRoute = signal(this.matchesPortalRoute(this.router.url));

  ngOnInit(): void {
    this.analyticsService.setUserType(UserTypeEnum.ADMIN);

    this.authSubscription = this.auth.user$.subscribe((user: User | null | undefined) => {
      if (user?.sub) {
        this.analyticsService.setUserId(user.sub);
      } else {
        this.analyticsService.setUserId(null);
      }
    });

    // Keep the layout choice in sync with the active route (mirrors the
    // customer app) — including hard-refresh: `matchesPortalRoute` is also
    // seeded synchronously above from `router.url` at construction time, so
    // chrome never flashes-then-hides on `/access-denied`.
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.isPortalRoute.set(this.matchesPortalRoute(event.urlAfterRedirects));
        if (this.isPortalRoute()) void this.applyTenantBranding();
      });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  /**
   * Guarded portal-route prefixes that render the admin shell. Almost all
   * admin features mount under `/auth/...`, but tenant management mounts at
   * top-level `/tenants` (`manageTenantUiRoutes`, path: 'tenants' — reached
   * from the Settings hub and the dashboard Tenants tile). Scanned
   * `app.routes.ts` 2026-07-07: `tenants` is the ONLY guarded non-`/auth/`
   * top-level route. If another one is ever added, list its prefix here or
   * the shell chrome silently disappears on it.
   */
  private static readonly PORTAL_ROUTE_PREFIXES: readonly string[] = [`${RouterUrlsEnum.AUTH}/`, '/tenants'];

  /**
   * Re-theme with the SIGNED-IN admin's own tenant branding. The app
   * initializer can only theme by the deployment's slug (pre-auth); the
   * admin's tenant id lives in the secure storage AdminRoleGuard populates.
   * A few `/auth/...` routes are unguarded (register/setup/license): there
   * storage may be empty (→ no-op, boot theme stays) or hold this tab's
   * session — logout clears it and reloads. Same source of truth as the
   * Branding page's save target.
   *
   * A failed fetch keeps the current theme and is retried on the next
   * portal navigation (brandedTenantId only advances on success).
   */
  private async applyTenantBranding(): Promise<void> {
    const tenantId = await this.adminAuthService.getTenantId();
    if (!tenantId || tenantId === this.brandedTenantId || tenantId === this.pendingTenantId) return;
    this.pendingTenantId = tenantId;
    const applied = await this.branding.init(tenantId, { keepCurrentOnError: true });
    this.pendingTenantId = null;
    if (applied) this.brandedTenantId = tenantId;
  }

  private matchesPortalRoute(url: string): boolean {
    return AppComponent.PORTAL_ROUTE_PREFIXES.some(prefix => url.startsWith(prefix));
  }
}
